'use client';

import React from 'react';
import { CheckCircle2, Music2, RotateCcw } from 'lucide-react';
import type { Song } from '@/lib/songs';

type BandConfirmation = {
  type: 'band';
  bandName: string;
  song: Song;
};

type SoloConfirmation = {
  type: 'solo';
  musicianName: string;
  instrument: string;
  songs: Song[];
};

type Props = {
  data: BandConfirmation | SoloConfirmation;
  onReset: () => void;
};

export default function ConfirmationScreen({ data, onReset }: Props) {
  return (
    <div className="text-center py-8 px-4 animate-[fadeIn_0.4s_ease]">
      <div className="flex justify-center mb-5">
        <div className="w-20 h-20 rounded-full bg-[#f5c842]/10 border-2 border-[#f5c842] flex items-center justify-center">
          <CheckCircle2 size={40} className="text-[#f5c842]" />
        </div>
      </div>

      <h2 className="font-display text-4xl text-[#f5c842] mb-2 tracking-wider">
        YOU&apos;RE ON THE LIST
      </h2>

      {data.type === 'band' ? (
        <div className="space-y-4 mt-6">
          <p className="text-[#f0ece3] text-lg font-semibold">{data.bandName}</p>
          {data.song.title ? (
            <div className="inline-flex items-center gap-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-5 py-3">
              <Music2 size={16} className="text-[#f5c842]" />
              <span className="text-[#f0ece3] font-medium">{data.song.title}</span>
              <span className="text-[#666]">—</span>
              <span className="text-[#888]">{data.song.artist}</span>
            </div>
          ) : null}
          <p className="text-[#666] text-sm max-w-sm mx-auto leading-relaxed">
            We&apos;ve got you down. Check your email — we&apos;ll confirm your slot and let you know any details about the night.
          </p>
        </div>
      ) : (
        <div className="space-y-4 mt-6">
          <p className="text-[#f0ece3] text-lg font-semibold">
            {data.musicianName} · <span className="text-[#e8386d]">{data.instrument}</span>
          </p>
          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-5 py-4 text-left max-w-sm mx-auto">
            <p className="text-[#888] text-xs font-semibold uppercase tracking-wider mb-3">
              Your songs ({data.songs.length})
            </p>
            <ul className="space-y-1.5">
              {data.songs.slice(0, 6).map((s) => (
                <li key={`conf-${s.id}`} className="flex items-center gap-2 text-sm">
                  <Music2 size={11} className="text-[#e8386d] shrink-0" />
                  <span className="text-[#f0ece3] truncate">{s.title}</span>
                  <span className="text-[#555] text-xs truncate">— {s.artist}</span>
                </li>
              ))}
              {data.songs.length > 6 && (
                <li className="text-[#555] text-xs pl-4">+{data.songs.length - 6} more</li>
              )}
            </ul>
          </div>
          <p className="text-[#666] text-sm max-w-sm mx-auto leading-relaxed">
            We&apos;ll get in touch when we find musicians playing songs that match yours. Keep an eye on your inbox.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
        <button
          type="button"
          onClick={onReset}
          className="btn-ghost flex items-center justify-center gap-2"
        >
          <RotateCcw size={15} />
          Sign Up Another
        </button>
      </div>
    </div>
  );
}